/* eslint-disable react-hooks/exhaustive-deps */
import { Button } from '@/components/button'
import { EventItem } from '@/components/common'
import { MainContent } from '@/components/content'
import { Close, Filter } from '@/components/icons'
import { AutocompleteAddress, AutocompleteLocationInput } from '@/components/input'
import { MainLayout } from '@/components/layouts'

import { RangeSlider } from '@/components/range-slider'
import { Search } from '@/components/search'
import { MainSidebar, MobileSidebar } from '@/components/sidebar'
import { EventItemLoading } from '@/components/skeleton'
import { eventTracker } from '@/config/trackingFunctions'
import { LIMIT, OFFSET, SCROLLING_HEIGHT_ABS_ERROR, SCROLLING_THROTTLING_DURATION } from '@/constants/Common'
import { TRACKING_TYPES, PAGE_NAMES } from '@/constants/TrackingConstants'
import { useDebounce } from '@/hooks/useDebounce'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { useUserDetails } from '@/hooks/useUserDetails'
import { authSelector } from '@/pages/auth/selectors'
import { appSelector } from '@/pages/home/selectors'
import { userSelector } from '@/pages/user/selectors'
import { getPlaceDetail } from '@/services/address'
import { EventStatus } from '@/types'
import { Switch } from '@headlessui/react'
import { throttle } from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getEvents, getMyEvents } from '../eventSlice'
import { eventSelector } from '../selectors'

export const Event = () => {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()
  const { token } = useAppSelector(authSelector)
  const { isShowDropdown } = useAppSelector(appSelector)
  const { user } = useAppSelector(userSelector)
  const { resEvents, isLoadingEvents } = useAppSelector(eventSelector)

  const userData = useUserDetails()

  const [isShowFilter, setIsShowFilter] = useState(false)
  const [isNearMe, setIsNearMe] = useState(false)
  const [geometry, setGeometry] = useState<{ lat: number; lng: number } | null>(null)
  const [location, setLocation] = useState<AutocompleteAddress>({ description: '', placeId: '' })
  const [offset, setOffset] = useState(OFFSET)
  const [searchText, setSearchText] = useState('')
  const [distance, setDistance] = useState(0)
  const [eventStatus, setEventStatus] = useState<EventStatus[]>([])
  const [isApplyFilter, setIsApplyFilter] = useState(false)

  const debouncedSearchText = useDebounce<string>(searchText || '*', 200)

  const toggleFilter = () => setIsShowFilter(!isShowFilter)

  const handleScroll = throttle(() => {
    if (
      Math.abs(window.innerHeight + document.documentElement.scrollTop - document.documentElement.offsetHeight) >
      SCROLLING_HEIGHT_ABS_ERROR
    ) {
      return
    }

    setOffset((pre) => pre + LIMIT)
  }, SCROLLING_THROTTLING_DURATION)

  const onApplyFilter = () => {
    setIsApplyFilter(!isApplyFilter)
    setOffset(OFFSET)
  }

  const onClearAll = () => {
    setIsApplyFilter(!isApplyFilter)
    setOffset(OFFSET)
    setLocation({ description: '', placeId: '' })
    setGeometry(null)
    setDistance(0)
    setEventStatus(['UPCOMING'])
  }

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value)
    setOffset(OFFSET)
  }

  const onGetPlaceDetail = async (address: AutocompleteAddress) => {
    const res = await getPlaceDetail(address?.placeId)
    setGeometry({ lat: res.result.geometry.location.lat, lng: res.result.geometry.location.lng })
  }

  const onGetMyEvents = () => {
    dispatch(
      getMyEvents({
        limit: 2,
        offset: OFFSET,
      })
    )
  }

  const onGetEvents = () => {
    dispatch(
      getEvents({
        filters: {
          durationStatus: eventStatus,
        },
        lat: geometry ? geometry?.lat : isNearMe ? (user?.addresses?.[0]?.latitude as number) : null,
        lng: geometry ? geometry?.lng : isNearMe ? (user?.addresses?.[0]?.longitude as number) : null,
        limit: 10,
        isNearMe: isNearMe,
        offset: offset,
        query: debouncedSearchText ? debouncedSearchText : '*',
        radiusInMeters: distance ? distance * 1609.34 : null,
      })
    )
    if (searchText) {
      eventTracker(TRACKING_TYPES.SEARCH_PERFORMED, {
        ...userData,
        'Page Name': PAGE_NAMES.BROWSE_EVENTS,
        Keyword: searchText,
      })
    }
  }

  const onChangeSwitch = (checked: boolean) => {
    setIsNearMe(checked)
    setOffset(OFFSET)
  }

  useEffect(() => {
    if (resEvents.hasMore) window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [resEvents])

  useEffect(() => {
    if (token) onGetMyEvents()
  }, [token])

  useEffect(() => {
    onGetEvents()
  }, [offset, debouncedSearchText, isApplyFilter, isNearMe, token])

  return (
    <>
      {!isShowDropdown && <MobileSidebar />}

      <MainLayout>
        <MainSidebar />
        <MainContent isShowFilter={isShowFilter}>
          <div className="md:flex md:flex-row md:justify-between">
            <div className={`flex flex-col mb-20 lg:mb-0 w-full`}>
              <h1 className="text-2xl md:text-[32px] md:leading-10 font-bold text-white">{t('event.events')}</h1>
              <div className="flex flex-col sm:flex-row gap-y-4 sm:gap-x-4 mt-4 md:mt-3">
                <div className="flex gap-x-2 md:gap-x-4 items-center">
                  <Search searchText={searchText} onChangeInput={onChangeInput} className="py-2 px-4 md:w-52 gap-x-2" />
                  <Button onClick={toggleFilter} className={`${isShowFilter ? 'bg-white' : ''} hover:opacity-50`}>
                    <Filter className={`${isShowFilter ? 'text-navy-900' : 'text-white'}`} />
                  </Button>
                </div>
                {token && (
                  <div className="flex items-center gap-x-3">
                    <Switch
                      checked={isNearMe}
                      onChange={(checked) => onChangeSwitch(checked)}
                      className={`${
                        isNearMe ? 'bg-primary-500' : 'bg-slate-300'
                      } relative inline-flex h-6 w-11 items-center rounded-full`}
                    >
                      <span
                        className={`${
                          isNearMe ? 'translate-x-6' : 'translate-x-1'
                        } inline-block h-4 w-4 transform rounded-full bg-white transition`}
                      />
                    </Switch>
                    <span className="text-sm text-white">{t('event.eventsNearMe')}</span>
                  </div>
                )}
              </div>

              <div className="mt-4">
                <h2 className="text-white font-semibold text-xl">{t('event.explore')}</h2>
                {resEvents?.hits?.map((item) => <EventItem key={item.leagueId} className="mt-3" event={item} />)}
                {isLoadingEvents && (
                  <>
                    <EventItemLoading className="mt-3" />
                    <EventItemLoading className="mt-3" />
                    <EventItemLoading className="mt-3" />
                    <EventItemLoading className="mt-3" />
                    <EventItemLoading className="mt-3" />
                  </>
                )}
                {resEvents && !resEvents?.hasMore && (
                  <div className="flex justify-center mt-4">
                    <span className="text-white font-semibold text-opacity-60">{t('common.noMoreResults')}</span>
                  </div>
                )}
              </div>
            </div>

            {isShowFilter && (
              <div
                className={`fixed z-50 top-0 w-screen h-screen p-4 lg:p-0 lg:pb-4 lg:px-2 bg-primary-400 lg:bg-transparent left-0 lg:left-auto lg:right-6 xl:right-9 lg:top-36 lg:w-[256px] xl:w-[296px] lg:max-h-[calc(100vh-144px)] overflow-y-auto overflow-x-hidden transition-all duration-500 ease-in-out flex flex-col gap-y-6`}
              >
                <div className="flex justify-between py-4">
                  <div className="flex items-center gap-x-2">
                    <Button onClick={toggleFilter} className="hover:opacity-50">
                      <Close className="text-white" />
                    </Button>
                    <span className="text-xl font-semibold text-white">{t('common.filters')}</span>
                  </div>
                  <button className="text-sm px-1 text-white font-semibold" onClick={onApplyFilter}>
                    {t('common.apply')}
                  </button>
                  <button className="text-sm px-1 text-white font-semibold" onClick={onClearAll}>
                    {t('common.clearAll')}
                  </button>
                </div>

                <AutocompleteLocationInput
                  label={t('common.location')}
                  placeholder={t('common.location')}
                  value={location}
                  setValue={(location: AutocompleteAddress) => {
                    setLocation(location)
                    if (!location.description) {
                      setGeometry(null)
                    }
                  }}
                  onChange={(location) => {
                    onGetPlaceDetail(location)
                  }}
                />

                <div className="flex flex-col gap-y-2">
                  <span className="text-white text-sm font-bold">{t('common.distance')}</span>
                  <div className="flex justify-between items-center gap-x-4 pt-12">
                    <div className="w-full">
                      <RangeSlider
                        className="h-4"
                        min={0}
                        max={100}
                        value={distance}
                        onChange={(e) => setDistance(e)}
                      />
                      <div className="flex justify-between mt-1">
                        <span className="text-white text-xs font-medium text-opacity-80">0</span>
                        <span className="text-white text-xs font-medium text-opacity-80">100</span>
                      </div>
                    </div>
                    <span className="text-white text-xs font-medium w-7">{t('common.mi')}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </MainContent>
      </MainLayout>
    </>
  )
}
